import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  AlertCircle, AlertTriangle, Calendar, CalendarX, Check, CheckCircle2,
  Clock, FileSpreadsheet, Filter, History, Info, Infinity as InfinityIcon, Pencil, Plus,
  RefreshCw, Search, Stethoscope, X,
} from 'lucide-react';
import { useSiteContext } from '../../SiteContext';
import ModifiedDutyManager from '../../managers/ModifiedDutyManager';
import { exportModifiedDutyEmployees } from '../../api/modifiedDuty';
import { getEmployees } from '../../api/employee';
import {
  CreateModifiedDutyEmployeeRequest, ModifiedDutyEmployee, ModifiedDutySaveResponse, UpdateModifiedDutyEmployeeRequest,
} from '../../interfaces/ModifiedDutyInterface';
import {
  ApprovalChange, approvalChangesNeedAttention, summarizeApprovalChanges,
} from '../../util/modifiedDutyApprovals';
import {
  ChipKey, ColumnFilters, DEFAULT_SORT, FilterCol, FormErrors, MdForm, MdRow, NO_SUPER, NOTE_MAX,
  SCOPES, SORT_LABEL, Scope, SortKey, SortState,
  clearFilter, dayDiff, deriveRow, emptyFilters, filterChips, fmtDate, fmtShort, inScope,
  isColumnFiltered, localTodayYMD, matchesFilters, matchesSearch, money, nextSort, openRecordFor,
  plural, scopeCounts, sortRows, supervisorCounts, supervisorOptions, toApiDate, validateForm, ymd,
} from './modifiedDutyHelpers';
import { MdPopover, MdTh, MdTip, downloadBlob, useHideOnScroll, useMdToast, usePopoverDismiss } from './mdShared';
import './ModifiedDutyPage.css';

/**
 * Modified Duty — v2 page (Field Ops › Modified Duty). WCDP only (config.showModifiedDuty).
 *
 * Built from docs/featureBriefs/design_handoff_modified_duty (README is the spec) and
 * docs/ModifiedDutyV2_DesignHandoff.md (locked decisions). Replaces the v1
 * src/Pages/ModifiedDutyPage.tsx; same route, permission and config gate.
 *
 * Everything (scope, search, header filters, sort) is client-side over the one
 * GET /admin/modifiedDutyEmployees payload. Export posts the on-screen ids in display
 * order to POST /admin/modifiedDutyEmployees/export, so the server needs no copy of
 * the filter logic.
 *
 * No layout shift: popovers and the notes tooltip portal to <body> (fixed); the chip
 * row has a fixed height; segment counts and the export label use tabular figures and
 * reserved widths; rows are a fixed height.
 */

/** TAS-679: a toast that also reports Modified Duty approval changes stays up longer. */
const TOAST_CHANGES_MS = 8000;

interface ColDef { key: SortKey | 'act'; label: string; width: string; num?: boolean; filter?: FilterCol }

const COLS: ColDef[] = [
  // Employee and Superintendent share the leftover width (names run long);
  // Notes is fixed and narrow because it's rarely used.
  { key: 'emp', label: 'Employee', width: 'auto', filter: 'emp' },
  { key: 'sup', label: 'Superintendent', width: 'auto', filter: 'sup' },
  { key: 'from', label: 'From', width: '112px', filter: 'from' },
  { key: 'to', label: 'To', width: '126px', filter: 'to' },
  { key: 'days', label: 'Days', width: '72px', num: true },
  { key: 'rate', label: 'Hourly Rate', width: '148px', num: true, filter: 'rate' },
  { key: 'notes', label: 'Notes', width: '180px', filter: 'notes' },
  { key: 'act', label: '', width: '56px' },
];

const POP_TITLE: Record<FilterCol, string> = {
  emp: 'Employee', sup: 'Superintendent', from: 'From date', to: 'To date', rate: 'Hourly rate', notes: 'Notes',
};

interface RosterEmployee { employeeID: number; altEmployeeID: string; name: string; active: boolean }

/* ====================================================================== *
 * Filter popover — portaled, fixed, anchored below the header cell.
 * ====================================================================== */

const FilterPopover: React.FC<{
  col: FilterCol;
  anchor: HTMLElement;
  filters: ColumnFilters;
  setFilters: React.Dispatch<React.SetStateAction<ColumnFilters>>;
  supOptions: string[];
  supCounts: Record<string, number>;
  onClose: () => void;
}> = ({ col, anchor, filters: f, setFilters, supOptions, supCounts, onClose }) => {
  const set = <K extends keyof ColumnFilters>(k: K, v: ColumnFilters[K]) => setFilters((prev) => ({ ...prev, [k]: v }));
  const toggleSup = (name: string, on: boolean) => setFilters((prev) => {
    const s = new Set(prev.sup);
    if (on) s.add(name); else s.delete(name);
    return { ...prev, sup: supOptions.filter((n) => s.has(n)) };
  });

  const text = (k: 'emp' | 'empId' | 'notes', ph: string, numeric = false) => (
    <input
      className="md-input"
      type="text"
      inputMode={numeric ? 'numeric' : undefined}
      value={f[k]}
      placeholder={ph}
      onChange={(e) => set(k, e.target.value)}
    />
  );
  const date = (k: 'fromA' | 'fromB' | 'toA' | 'toB') => (
    <input className="md-input" type="date" value={f[k]} onChange={(e) => set(k, e.target.value)} />
  );
  const rate = (k: 'rateMin' | 'rateMax') => (
    <span className="md-money">
      <i>$</i>
      <input
        className="md-input"
        type="number"
        min={0}
        step={0.25}
        placeholder="0.00"
        value={f[k]}
        onChange={(e) => set(k, e.target.value)}
      />
    </span>
  );

  let body: React.ReactNode = null;
  if (col === 'emp') {
    body = (
      <>
        <label className="md-field"><span className="md-flab">Name contains</span>{text('emp', 'e.g. Alvarez')}</label>
        <label className="md-field"><span className="md-flab">Employee ID contains</span>{text('empId', 'e.g. 317', true)}</label>
      </>
    );
  } else if (col === 'notes') {
    body = <label className="md-field"><span className="md-flab">Notes contain</span>{text('notes', 'e.g. lifting')}</label>;
  } else if (col === 'sup') {
    body = (
      <div className="md-checks">
        {supOptions.map((n) => (
          <label key={n} className="md-check">
            <input type="checkbox" checked={f.sup.includes(n)} onChange={(e) => toggleSup(n, e.target.checked)} />
            <span className={`md-check-l${n === NO_SUPER ? ' muted' : ''}`}>{n === NO_SUPER ? '(none) — no supervisor' : n}</span>
            <span className="md-cc">{supCounts[n] ?? 0}</span>
          </label>
        ))}
      </div>
    );
  } else if (col === 'from' || col === 'to') {
    body = (
      <>
        <div className="md-prow">
          <label className="md-field"><span className="md-flab">On or after</span>{date(col === 'from' ? 'fromA' : 'toA')}</label>
          <label className="md-field"><span className="md-flab">On or before</span>{date(col === 'from' ? 'fromB' : 'toB')}</label>
        </div>
        {col === 'to' && (
          <>
            <label className="md-check md-check-solo">
              <input type="checkbox" checked={f.toOpen} onChange={(e) => set('toOpen', e.target.checked)} />
              <span className="md-check-l">Open (no end date)</span>
            </label>
            <div className="md-phelp">With a date range too, shows rows in the range <b>or</b> open.</div>
          </>
        )}
      </>
    );
  } else if (col === 'rate') {
    body = (
      <div className="md-prow">
        <label className="md-field"><span className="md-flab">Min</span>{rate('rateMin')}</label>
        <label className="md-field"><span className="md-flab">Max</span>{rate('rateMax')}</label>
      </div>
    );
  }

  const wide = col === 'from' || col === 'to' || col === 'rate';
  return (
    <MdPopover
      anchor={anchor}
      title={POP_TITLE[col]}
      wide={wide}
      focusKey={col}
      canClear={isColumnFiltered(f, col)}
      onClear={() => setFilters((prev) => clearFilter(prev, col))}
      onClose={onClose}
    >
      {body}
    </MdPopover>
  );
};

/* ====================================================================== *
 * Employee picker (Add only) — searchable combobox over /admin/employees.
 * Same data source as the v1 EmployeeSelector, but searches name or ID.
 * The list overlays the form (absolute), so nothing below it moves.
 * ====================================================================== */

const EmployeePicker: React.FC<{
  employees: RosterEmployee[] | null;
  loadError: boolean;
  value: number | null;
  onChange: (id: number | null) => void;
  invalid: boolean;
  inputRef: React.RefObject<HTMLInputElement>;
}> = ({ employees, loadError, value, onChange, invalid, inputRef }) => {
  const selected = useMemo(() => employees?.find((e) => e.employeeID === value) ?? null, [employees, value]);
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [hi, setHi] = useState(0);
  const listRef = useRef<HTMLDivElement>(null);

  const label = selected ? `${selected.name} · ${selected.altEmployeeID}` : '';
  const shown = open ? query : label;

  const matches = useMemo(() => {
    if (!employees) return [];
    const q = query.trim().toLowerCase();
    const list = q
      ? employees.filter((e) => e.name.toLowerCase().includes(q) || e.altEmployeeID.toLowerCase().includes(q))
      : employees;
    return list.slice(0, 60);
  }, [employees, query]);

  useEffect(() => { setHi(0); }, [query]);
  useEffect(() => {
    const el = listRef.current?.querySelector<HTMLElement>(`[data-i="${hi}"]`);
    el?.scrollIntoView({ block: 'nearest' });
  }, [hi]);

  const choose = (e: RosterEmployee) => {
    onChange(e.employeeID);
    setOpen(false);
    setQuery('');
  };

  const onKey = (ev: React.KeyboardEvent<HTMLInputElement>) => {
    if (ev.key === 'ArrowDown') { ev.preventDefault(); setOpen(true); setHi((h) => Math.min(matches.length - 1, h + 1)); }
    else if (ev.key === 'ArrowUp') { ev.preventDefault(); setHi((h) => Math.max(0, h - 1)); }
    else if (ev.key === 'Enter') {
      if (open && matches[hi]) { ev.preventDefault(); choose(matches[hi]); }
    } else if (ev.key === 'Escape' && open) {
      ev.preventDefault();
      ev.stopPropagation();
      setOpen(false);
      setQuery('');
    }
  };

  const placeholder = loadError
    ? "Couldn't load employees"
    : employees ? 'Search by name or employee ID…' : 'Loading employees…';

  return (
    <div className="md-combo">
      <Search className="md-combo-ic" />
      <input
        ref={inputRef}
        id="mdEmp"
        className={`md-input${invalid ? ' bad' : ''}`}
        type="text"
        role="combobox"
        aria-expanded={open}
        aria-controls="mdEmpList"
        aria-autocomplete="list"
        autoComplete="off"
        placeholder={placeholder}
        disabled={!employees}
        value={shown}
        onFocus={() => { setOpen(true); setQuery(''); }}
        onBlur={() => { setOpen(false); setQuery(''); }}
        onChange={(e) => { setQuery(e.target.value); setOpen(true); if (value != null) onChange(null); }}
        onKeyDown={onKey}
      />
      {open && employees && (
        <div className="md-combo-list" id="mdEmpList" role="listbox" ref={listRef}>
          {matches.length === 0 && <div className="md-combo-empty">No employees match “{query.trim()}”</div>}
          {matches.map((e, i) => (
            <div
              key={e.employeeID}
              data-i={i}
              role="option"
              aria-selected={e.employeeID === value}
              className={`md-combo-opt${i === hi ? ' hi' : ''}${e.employeeID === value ? ' sel' : ''}`}
              onMouseDown={(ev) => { ev.preventDefault(); choose(e); }}
              onMouseEnter={() => setHi(i)}
            >
              <span className="nm">{e.name}</span>
              <span className="id">{e.altEmployeeID}</span>
              {!e.active && <span className="md-tag">Inactive</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

/* ====================================================================== *
 * Add / Edit modal
 * ====================================================================== */

interface ModalState { mode: 'add' | 'edit'; rec: ModifiedDutyEmployee | null }

const MdModal: React.FC<{
  state: ModalState;
  today: string;
  records: ModifiedDutyEmployee[];
  employees: RosterEmployee[] | null;
  employeesError: boolean;
  onClose: () => void;
  onSaved: (msg: string, approvalChanges?: ApprovalChange[]) => void;
}> = ({ state, today, records, employees, employeesError, onClose, onSaved }) => {
  const { createPopup } = useSiteContext();
  const rec = state.rec;
  const [form, setForm] = useState<MdForm>(() => ({
    mode: state.mode,
    employeeID: rec?.employeeID ?? null,
    from: ymd(rec?.fromDate) ?? '',
    to: ymd(rec?.toDate) ?? '',
    rate: rec ? Number(rec.hourlyRate).toFixed(2) : '',
    notes: rec?.notes ?? '',
  }));
  const [tried, setTried] = useState(false);
  const [saving, setSaving] = useState(false);
  const origOpen = state.mode === 'edit' && !!rec && !ymd(rec.toDate);

  const empRef = useRef<HTMLInputElement>(null);
  const fromRef = useRef<HTMLInputElement>(null);
  const toRef = useRef<HTMLInputElement>(null);
  const rateRef = useRef<HTMLInputElement>(null);

  const errs: FormErrors = tried ? validateForm(form) : {};
  const openRec = state.mode === 'add' ? openRecordFor(records, form.employeeID) : null;
  const warn = openRec ? `Already has an open record from ${fmtDate(openRec.fromDate)}. Consider ending it first.` : '';

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && !saving) onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose, saving]);

  const set = <K extends keyof MdForm>(k: K, v: MdForm[K]) => setForm((p) => ({ ...p, [k]: v }));

  const save = async () => {
    setTried(true);
    const e = validateForm(form);
    if (Object.keys(e).length) {
      const first = e.emp ? empRef : e.from ? fromRef : e.to ? toRef : rateRef;
      first.current?.focus({ preventScroll: true });
      return;
    }
    const hourlyRate = Math.round(parseFloat(form.rate) * 100) / 100;
    const notes = form.notes.trim().slice(0, NOTE_MAX);
    setSaving(true);
    try {
      let res: unknown;
      if (state.mode === 'add') {
        const body: CreateModifiedDutyEmployeeRequest = {
          employeeID: form.employeeID as number,
          fromDate: toApiDate(form.from),
          toDate: toApiDate(form.to),
          hourlyRate,
          notes,
          // TAS-679: the API re-checks the current + prior pay period's approvals against
          // the saved record; `today` picks the current period (same as /export).
          today,
        };
        res = await ModifiedDutyManager.addModifiedDutyEmployee(body);
      } else {
        const body: UpdateModifiedDutyEmployeeRequest = {
          fromDate: toApiDate(form.from),
          toDate: toApiDate(form.to),
          hourlyRate,
          notes,
          today,
        };
        res = await ModifiedDutyManager.editModifiedDutyEmployee((rec as ModifiedDutyEmployee).modifiedDutyEmployeesID, body);
      }
      // The API answers 200 { success: false, errorMessage } on a DB error.
      const r = res as { success?: boolean; errorMessage?: string } | null;
      if (r && r.success === false) throw new Error(r.errorMessage || 'The server rejected the change.');
      const name = state.mode === 'add'
        ? employees?.find((x) => x.employeeID === form.employeeID)?.name ?? 'employee'
        : `${rec?.employee?.firstName ?? ''} ${rec?.employee?.lastName ?? ''}`.trim();
      onSaved(
        state.mode === 'add' ? `Added modified duty for ${name}` : `Saved ${name}`,
        (res as ModifiedDutySaveResponse | null)?.approvalChanges,
      );
    } catch (err: any) {
      createPopup(state.mode === 'add' ? 'Add failed' : 'Save failed', err?.message || 'Could not save the modified duty record.');
    } finally {
      setSaving(false);
    }
  };

  const emp = rec?.employee;
  const sub = state.mode === 'add'
    ? 'Hours worked in this date range are paid at the modified duty rate.'
    : (
      <>
        {`${emp?.firstName ?? ''} ${emp?.lastName ?? ''}`.trim()} · <span className="mono">{emp?.altEmployeeID}</span>
        {emp?.supervisorName ? ` · ${emp.supervisorName}` : ''}
      </>
    );

  let strip: React.ReactNode = null;
  if (origOpen && rec) {
    const fromYmd = ymd(rec.fromDate) ?? form.from;
    const since = Math.max(0, dayDiff(fromYmd, today) + 1);
    if (form.to === today) {
      strip = (
        <div className="md-strip done">
          <CheckCircle2 />
          <div className="grow"><b>Ends today, {fmtDate(today)}.</b> Moves to Inactive tomorrow once saved.</div>
          <button type="button" className="md-linkbtn" onClick={() => set('to', '')}>Undo</button>
        </div>
      );
    } else {
      strip = (
        <div className="md-strip">
          <InfinityIcon />
          <div className="grow">
            {form.to
              ? <>Open since {fmtDate(fromYmd)}. End date set to {fmtDate(form.to)}.</>
              : <><b>Open-ended</b> since {fmtDate(fromYmd)} · {plural(since, 'day')}</>}
          </div>
          <button type="button" className="md-btn sm" onClick={() => set('to', today)}><CalendarX />Set end date today</button>
        </div>
      );
    }
  }

  const msg = (k: 'from' | 'to' | 'rate', hint = '') => (
    <div className={`md-err${errs[k] ? ' on' : ''}`}>
      {errs[k] ? <><AlertCircle />{errs[k]}</> : hint}
    </div>
  );

  return (
    <div className="md-modal-bg" onMouseDown={(e) => { if (e.target === e.currentTarget && !saving) onClose(); }}>
      <div className="md-modal" role="dialog" aria-modal="true" aria-labelledby="mdModalTitle">
        <div className="md-mhead">
          <div className="mi">{state.mode === 'add' ? <Stethoscope /> : <Pencil />}</div>
          <div className="grow">
            <h3 id="mdModalTitle">{state.mode === 'add' ? 'Add Modified Duty' : 'Edit Modified Duty'}</h3>
            <div className="ms">{sub}</div>
          </div>
          <button type="button" className="x" onClick={onClose} aria-label="Close" disabled={saving}><X /></button>
        </div>
        <div className="md-mbody">
          {state.mode === 'add' && (
            <div className="md-field">
              <label className="md-flab" htmlFor="mdEmp">Employee <span className="md-req">*</span></label>
              <EmployeePicker
                employees={employees}
                loadError={employeesError}
                value={form.employeeID}
                onChange={(id) => set('employeeID', id)}
                invalid={!!errs.emp}
                inputRef={empRef}
              />
              <div className={`md-err${errs.emp ? ' on' : warn ? ' warn on' : ''}`}>
                {errs.emp ? <><AlertCircle />{errs.emp}</> : warn ? <><Info />{warn}</> : ''}
              </div>
            </div>
          )}
          {strip}
          <div className="md-row2">
            <div className="md-field">
              <label className="md-flab" htmlFor="mdFrom">From <span className="md-req">*</span></label>
              <input
                ref={fromRef}
                id="mdFrom"
                className={`md-input${errs.from ? ' bad' : ''}`}
                type="date"
                value={form.from}
                onChange={(e) => set('from', e.target.value)}
              />
              {msg('from')}
            </div>
            <div className="md-field">
              <label className="md-flab" htmlFor="mdTo">To <span className="md-opt">optional</span></label>
              <input
                ref={toRef}
                id="mdTo"
                className={`md-input${errs.to ? ' bad' : ''}`}
                type="date"
                value={form.to}
                onChange={(e) => set('to', e.target.value)}
              />
              {msg('to', "Leave blank if there's no end date yet.")}
            </div>
          </div>
          <div className="md-field md-rate">
            <label className="md-flab" htmlFor="mdRate">Hourly rate <span className="md-req">*</span></label>
            <span className="md-money lg">
              <i>$</i>
              <input
                ref={rateRef}
                id="mdRate"
                className={`md-input${errs.rate ? ' bad' : ''}`}
                type="number"
                min={0}
                step={0.25}
                placeholder="0.00"
                value={form.rate}
                onChange={(e) => set('rate', e.target.value)}
              />
            </span>
            {msg('rate')}
          </div>
          <div className="md-field">
            <label className="md-flab" htmlFor="mdNotes">Notes</label>
            <textarea
              id="mdNotes"
              className="md-input"
              rows={3}
              maxLength={NOTE_MAX}
              placeholder="Restrictions, doctor's notes…"
              value={form.notes}
              onChange={(e) => set('notes', e.target.value)}
            />
          </div>
        </div>
        <div className="md-mfoot">
          <button type="button" className="md-btn ghost" onClick={onClose} disabled={saving}>Cancel</button>
          <button type="button" className="md-btn primary" onClick={save} disabled={saving}>
            {saving
              ? <><span className="md-spin light" />Saving…</>
              : state.mode === 'add' ? <><Plus />Add</> : <><Check />Save changes</>}
          </button>
        </div>
      </div>
    </div>
  );
};

/* ====================================================================== *
 * The page
 * ====================================================================== */

const ModifiedDutyPage: React.FC = () => {
  const { createPopup } = useSiteContext();

  const [records, setRecords] = useState<ModifiedDutyEmployee[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [scope, setScope] = useState<Scope>('Active');
  const [query, setQuery] = useState('');
  const [filters, setFilters] = useState<ColumnFilters>(emptyFilters);
  const [sort, setSort] = useState<SortState>(DEFAULT_SORT);
  const [pop, setPop] = useState<{ col: FilterCol; anchor: HTMLElement } | null>(null);
  const [tip, setTip] = useState<{ text: string; rect: DOMRect } | null>(null);
  const [modal, setModal] = useState<ModalState | null>(null);
  const [exporting, setExporting] = useState(false);
  const { toast, toastOn, showToast } = useMdToast();

  const [employees, setEmployees] = useState<RosterEmployee[] | null>(null);
  const [employeesError, setEmployeesError] = useState(false);

  const scrollRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  // "Today" is the viewer's local calendar date; re-read on each render so a page
  // left open past midnight re-classifies rows on the next interaction.
  const today = localTodayYMD();

  /* ---------------- data ---------------- */

  const loadSeq = useRef(0);
  const load = useCallback(async (quiet = false) => {
    const seq = ++loadSeq.current;
    if (!quiet) setLoading(true);
    setLoadError(null);
    try {
      const rows = await ModifiedDutyManager.getModifiedDutyEmployees();
      if (seq === loadSeq.current) setRecords(Array.isArray(rows) ? rows : []);
    } catch (err: any) {
      if (seq === loadSeq.current) setLoadError(err?.message || "Couldn't load modified duty records.");
    } finally {
      if (seq === loadSeq.current) setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const loadEmployees = useCallback(async () => {
    if (employees) return;
    setEmployeesError(false);
    try {
      const res = await getEmployees();
      const list: RosterEmployee[] = (res?.data ?? []).map((e: any) => ({
        employeeID: e.employeeID,
        altEmployeeID: String(e.altEmployeeID ?? ''),
        name: `${e.firstName ?? ''} ${e.lastName ?? ''}`.trim(),
        active: e.active !== false && e.active !== 0,
      }));
      list.sort((a, b) => (a.active === b.active ? a.name.localeCompare(b.name) : a.active ? -1 : 1));
      setEmployees(list);
    } catch {
      setEmployeesError(true);
    }
  }, [employees]);

  /* ---------------- derived ---------------- */

  const allRows = useMemo(() => records.map((r) => deriveRow(r, today)), [records, today]);
  const matched = useMemo(
    () => allRows.filter((r) => matchesSearch(r, query) && matchesFilters(r, filters)),
    [allRows, query, filters],
  );
  const counts = useMemo(() => scopeCounts(matched), [matched]);
  const scopeRows = useMemo(() => allRows.filter((r) => inScope(r, scope)), [allRows, scope]);
  const rows = useMemo(() => sortRows(matched.filter((r) => inScope(r, scope)), sort), [matched, scope, sort]);
  const chips = useMemo(() => filterChips(filters), [filters]);
  const supOptions = useMemo(() => supervisorOptions(allRows), [allRows]);
  const supCounts = useMemo(() => supervisorCounts(scopeRows), [scopeRows]);

  /* ---------------- popover ---------------- */

  const closePop = useCallback(() => setPop(null), []);
  usePopoverDismiss(!!pop, closePop);

  const togglePop = (col: FilterCol, btn: HTMLButtonElement) => {
    const th = btn.closest('th') as HTMLElement | null;
    setPop((p) => (p?.col === col ? null : { col, anchor: th ?? btn }));
  };

  /* ---------------- tooltip ---------------- */

  const hideTip = useCallback(() => setTip(null), []);
  useHideOnScroll(!!tip, hideTip);

  const showTip = (el: HTMLElement, text: string) => {
    if (el.scrollHeight <= el.clientHeight + 1) { setTip(null); return; }
    setTip({ text, rect: el.getBoundingClientRect() });
  };

  /* ---------------- actions ---------------- */

  const changeScope = (s: Scope) => {
    setScope(s);
    if (scrollRef.current) scrollRef.current.scrollTop = 0;
  };

  const clearAll = (withSearch: boolean) => {
    setFilters(emptyFilters());
    if (withSearch) setQuery('');
  };

  const removeChip = (key: ChipKey) => setFilters((f) => clearFilter(f, key));

  const openAdd = () => {
    setPop(null);
    setModal({ mode: 'add', rec: null });
    loadEmployees();
  };
  const openEdit = (rec: ModifiedDutyEmployee) => {
    setPop(null);
    setModal({ mode: 'edit', rec });
  };
  const closeModal = useCallback(() => setModal(null), []);
  const onSaved = useCallback((msg: string, approvalChanges?: ApprovalChange[]) => {
    setModal(null);
    load(true);
    // TAS-679: approvalChanges is non-empty only on WCDP with the Modified Duty approver
    // gate on — everywhere else this is the plain save toast it always was.
    const changes = summarizeApprovalChanges(approvalChanges);
    if (!changes) {
      showToast(<><CheckCircle2 /><span>{msg}</span></>);
      return;
    }
    const attention = approvalChangesNeedAttention(approvalChanges);
    showToast(
      <>
        {attention ? <AlertTriangle className="md-toast-warn" /> : <CheckCircle2 />}
        <span>{msg}<span className="md-toast-sub">{changes}</span></span>
      </>,
      TOAST_CHANGES_MS,
    );
  }, [load, showToast]);

  const doExport = async () => {
    if (!rows.length || exporting) return;
    const n = rows.length;
    setExporting(true);
    try {
      const { blob, fileName } = await exportModifiedDutyEmployees({ ids: rows.map((r) => r.id), scope, today });
      downloadBlob(blob, fileName);
      showToast(<><CheckCircle2 /><span><b>{fileName}</b> downloaded · {plural(n, 'row')}</span></>);
    } catch (err: any) {
      createPopup('Export failed', err?.message || 'Could not export modified duty records.');
    } finally {
      setExporting(false);
    }
  };

  /* ---------------- render pieces ---------------- */

  const head = (c: ColDef) => {
    if (c.key === 'act') return <th key="act" className="md-th-act"><span className="md-sr">Edit</span></th>;
    const key = c.key;
    const filterCol = c.filter;
    return (
      <MdTh
        key={key}
        label={c.label}
        num={c.num}
        sorted={sort.key === key}
        dir={sort.dir}
        filtered={filterCol ? isColumnFiltered(filters, filterCol) : false}
        popOpen={!!filterCol && pop?.col === filterCol}
        onSort={() => setSort((s) => nextSort(s, key))}
        onFilter={filterCol ? (btn) => togglePop(filterCol, btn) : undefined}
      />
    );
  };

  const row = (x: MdRow) => {
    let toCell: React.ReactNode;
    if (x.open) {
      toCell = <><span className="md-open"><InfinityIcon />Open</span><span className="sub">No end date</span></>;
    } else if (x.endsSoon) {
      toCell = (
        <>
          <span className="mono">{fmtDate(x.to)}</span>
          <span className="sub md-soon"><Clock />{x.left === 0 ? 'Ends today' : `Ends in ${plural(x.left ?? 0, 'day')}`}</span>
        </>
      );
    } else {
      toCell = <><span className="mono">{fmtDate(x.to)}</span>{x.inactive && <span className="sub">Ended</span>}</>;
    }
    return (
      <tr key={x.id} className={`${x.inactive ? 'ended' : ''}${x.upcoming ? ' upcoming' : ''}`}>
        <td>
          <div className="md-emp">
            <span className="nm" title={x.name}>{x.name || '—'}</span>
            <span className="sub mono">{x.empID}</span>
          </div>
        </td>
        <td className="md-td-sup" title={x.sup ?? undefined}>{x.sup ?? <span className="dash">—</span>}</td>
        <td>
          <div className="md-cell2">
            <span className="mono">{fmtDate(x.from)}</span>
            {x.upcoming && <span className="sub md-up"><Calendar />Starts {fmtShort(x.from)}</span>}
          </div>
        </td>
        <td><div className="md-cell2">{toCell}</div></td>
        <td className="num">
          <div className="md-cell2 r">
            {x.days == null
              ? <span className="dash">—</span>
              : <><span className="mono">{x.days}</span>{!x.inactive && <span className="sub">so far</span>}</>}
          </div>
        </td>
        <td className="num"><span className="mono">{money(x.rate)}</span></td>
        <td>
          {x.notes.trim()
            ? (
              <div
                className="md-note"
                tabIndex={0}
                onMouseEnter={(e) => showTip(e.currentTarget, x.notes)}
                onMouseLeave={() => setTip(null)}
                onFocus={(e) => showTip(e.currentTarget, x.notes)}
                onBlur={() => setTip(null)}
              >
                {x.notes}
              </div>
            )
            : <span className="dash">—</span>}
        </td>
        <td className="cen">
          <button
            type="button"
            className="md-iconbtn edit"
            title="Edit"
            aria-label={`Edit ${x.name} ${fmtDate(x.from)}`}
            onClick={() => openEdit(x.rec)}
          >
            <Pencil />
          </button>
        </td>
      </tr>
    );
  };

  const emptyBody = () => {
    const filteredOut = chips.length > 0 || !!query.trim();
    let content: React.ReactNode;
    if (loading) {
      content = <div className="md-empty"><div className="et">Loading modified duty records…</div></div>;
    } else if (loadError) {
      content = (
        <div className="md-empty">
          <div className="eic"><AlertCircle /></div>
          <div className="et">Couldn't load modified duty records</div>
          <p>{loadError}</p>
          <button type="button" className="md-btn sm" onClick={() => load()}><RefreshCw />Retry</button>
        </div>
      );
    } else if (filteredOut) {
      const where = scope === 'All' ? 'any record' : scope;
      content = (
        <div className="md-empty">
          <div className="eic"><Filter /></div>
          <div className="et">No records match these filters</div>
          <p>
            Nothing in {where} matches{query.trim() ? ` “${query.trim()}”` : ''}
            {chips.length ? ` with ${plural(chips.length, 'column filter')}` : ''}.
          </p>
          <button type="button" className="md-btn sm" onClick={() => clearAll(true)}><X />Clear filters</button>
        </div>
      );
    } else if (scope === 'Active') {
      content = (
        <div className="md-empty">
          <div className="eic"><Stethoscope /></div>
          <div className="et">No one is currently on modified duty</div>
          <p>Records with no end date, or an end date today or later, show up here.</p>
          <div className="md-empty-act">
            <button type="button" className="md-btn sm" onClick={() => changeScope('Inactive')}><History />View inactive records</button>
            <button type="button" className="md-btn sm primary" onClick={openAdd}><Plus />Add Modified Duty</button>
          </div>
        </div>
      );
    } else {
      content = (
        <div className="md-empty">
          <div className="eic"><Stethoscope /></div>
          <div className="et">No modified duty records yet</div>
          <p>Add the first one to start tracking modified duty pay.</p>
          <button type="button" className="md-btn sm primary" onClick={openAdd}><Plus />Add Modified Duty</button>
        </div>
      );
    }
    return <tr className="md-empty-row"><td colSpan={COLS.length}>{content}</td></tr>;
  };

  const n = rows.length;
  const scopeTotal = scopeRows.length;

  return (
    <div className="md-page">
      <div className="md-head">
        <div className="ph-ic"><Stethoscope /></div>
        <div className="grow">
          <h1>Modified Duty</h1>
          <div className="ph-sub">
            Employees on modified duty are paid the rate below for hours worked between From and To.
            Leave To blank while there's no end date yet.
          </div>
        </div>
        <div className="ph-actions">
          <button type="button" className="md-btn md-export" onClick={doExport} disabled={!n || exporting || loading}>
            {exporting
              ? <><span className="md-spin" /><span>Exporting…</span></>
              : <><FileSpreadsheet /><span>Export {plural(n, 'row')}</span></>}
          </button>
          <button type="button" className="md-btn primary" onClick={openAdd}><Plus />Add Modified Duty</button>
        </div>
      </div>

      <div className="md-card">
        <div className="md-bar">
          <div className="md-seg" role="radiogroup" aria-label="Status">
            {SCOPES.map((s) => (
              <button
                key={s}
                type="button"
                role="radio"
                aria-checked={scope === s}
                className={scope === s ? 'on' : ''}
                onClick={() => changeScope(s)}
              >
                {s}<span className="md-cnt">{counts[s]}</span>
              </button>
            ))}
          </div>
          <div className="md-search">
            <Search className="md-search-ic" />
            <input
              ref={searchRef}
              type="text"
              value={query}
              placeholder="Search name, ID, superintendent or notes…"
              aria-label="Search"
              onChange={(e) => setQuery(e.target.value)}
            />
            <button
              type="button"
              className="md-qx"
              aria-label="Clear search"
              style={{ visibility: query ? 'visible' : 'hidden' }}
              onClick={() => { setQuery(''); searchRef.current?.focus(); }}
            >
              <X />
            </button>
          </div>
        </div>

        <div className="md-chips">
          <div className="md-chips-l">
            {chips.length
              ? (
                <>
                  {chips.map((c) => (
                    <span key={c.key} className="md-chip" title={c.title}>
                      <b>{c.label}</b>{c.value}
                      <button type="button" aria-label={`Remove ${c.label} filter`} onClick={() => removeChip(c.key)}><X /></button>
                    </span>
                  ))}
                  <button type="button" className="md-linkbtn md-clearall" onClick={() => clearAll(false)}>Clear all</button>
                </>
              )
              : <span className="md-hint-row"><Filter />Filter any column from the icon in its header.</span>}
          </div>
          <div className="md-shown" aria-live="polite">
            {n === scopeTotal ? plural(n, 'record') : <><b>{n}</b> of {scopeTotal}</>}
          </div>
        </div>

        <div className="md-scroll" ref={scrollRef}>
          <table className="md-tbl">
            <colgroup>{COLS.map((c) => <col key={c.key} style={{ width: c.width }} />)}</colgroup>
            <thead><tr>{COLS.map(head)}</tr></thead>
            <tbody>{n && !loading ? rows.map(row) : emptyBody()}</tbody>
          </table>
        </div>

        <div className="md-foot">
          <span>Sorted by <b>{SORT_LABEL[sort.key]}</b> {sort.dir === 'asc' ? 'ascending' : 'descending'}</span>
          <span className="grow" />
          <span className="md-legend">
            <span className="md-open sm"><InfinityIcon />Open</span>no end date
            <span className="md-soon"><Clock />Ends soon</span>within 7 days
          </span>
        </div>
      </div>

      {pop && (
        <FilterPopover
          col={pop.col}
          anchor={pop.anchor}
          filters={filters}
          setFilters={setFilters}
          supOptions={supOptions}
          supCounts={supCounts}
          onClose={closePop}
        />
      )}

      {tip && <MdTip rect={tip.rect}>{tip.text}</MdTip>}

      {modal && (
        <MdModal
          key={modal.rec?.modifiedDutyEmployeesID ?? 'add'}
          state={modal}
          today={today}
          records={records}
          employees={employees}
          employeesError={employeesError}
          onClose={closeModal}
          onSaved={onSaved}
        />
      )}

      <div className={`md-toast${toastOn ? ' on' : ''}`} role="status" aria-live="polite">{toast}</div>
    </div>
  );
};

export default ModifiedDutyPage;
